import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AppBar } from "../../components/AppShell";
import { request } from "../../lib/api";
import type { AdminStats } from "../../lib/types";

const ROLE_CARDS: { role: string; bg: string; color: string; icon: string; label: string }[] = [
  { role: "medic", bg: "var(--c-primary-light)", color: "var(--c-primary-dark)", icon: "fa-user-doctor", label: "Medici" },
  { role: "rezident", bg: "#fdf1de", color: "#8a5a04", icon: "fa-stethoscope", label: "Rezidenți" },
  { role: "asistent", bg: "#ede4fa", color: "#4a2c91", icon: "fa-hand-holding-medical", label: "Asistenți" },
];

export function AdminDashboard() {
  const { data } = useQuery({ queryKey: ["admin", "stats"], queryFn: () => request<AdminStats>("/admin/stats") });

  return (
    <>
      <AppBar crumbs={[{ label: "Tablou de bord" }]} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Tablou de bord</h1>
            <p>Privire de ansamblu asupra platformei.</p>
          </div>
          <div className="page-actions">
            <Link to="/admin/cursuri" className="btn btn-primary"><i className="fa-solid fa-plus"></i> Curs nou</Link>
          </div>
        </div>

        <div className="kpi-grid">
          <Kpi label="Studenți totali" value={data?.students_total ?? "…"} delta={data ? `${data.students_active} activi` : ""} icon="fa-user-graduate" />
          <Kpi label="Cursuri publicate" value={data?.courses_published ?? "…"} delta={data ? `${data.courses_draft} schiță` : ""} icon="fa-book" />
          <Kpi label="Examene susținute" value={data?.exam_attempts_total ?? "…"} icon="fa-clipboard-check" />
          <Kpi label="Rată promovare" value={data ? `${data.pass_rate}%` : "…"} icon="fa-trophy" />
        </div>

        <div className="grid-2">
          <div className="card">
            <h3>Studenți pe categorie</h3>
            <div>
              {ROLE_CARDS.map((c) => {
                const n = data?.students_by_role[c.role] ?? 0;
                return (
                  <div key={c.role} style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, border: "1px solid var(--c-line-2)", borderRadius: 10, marginBottom: 8 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: c.bg, color: c.color, display: "grid", placeItems: "center", fontSize: "1.1rem" }}>
                      <i className={`fa-solid ${c.icon}`}></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong>{c.label}</strong><br />
                      <small className="muted">{n} {n === 1 ? "student" : "studenți"}</small>
                    </div>
                    <strong style={{ fontSize: "1.4rem", color: c.color }}>{n}</strong>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <h3>Acțiuni rapide</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Link to="/admin/studenti" className="btn btn-ghost"><i className="fa-solid fa-users"></i> Gestionează studenți</Link>
              <Link to="/admin/cursuri" className="btn btn-ghost"><i className="fa-solid fa-book"></i> Gestionează cursuri</Link>
              <Link to="/admin/administratori" className="btn btn-ghost"><i className="fa-solid fa-user-shield"></i> Gestionează administratori</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, delta, icon }: { label: string; value: string | number; delta?: string; icon: string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {delta && <div className="kpi-delta up">{delta}</div>}
      <div className="kpi-icon"><i className={`fa-solid ${icon}`}></i></div>
    </div>
  );
}
